// @flow
import React, { useEffect, useContext, useReducer } from 'react';
import loadable from '@loadable/component';
import bem from '@justpark/helpers/src/bem/bem';
import Tabs, { TabItem } from '@justpark/ui/src/components/Tabs';
import type { Dayjs } from 'dayjs';
import { useLazyQuery } from '@apollo/client';
import BrowserContext from '../../context/BrowserContext';
import MonthlyDateSelector from './MonthlyDateSelector';
import useQueryStringTab from '../../hooks/useQueryStringTab';
import FixedTermDateSelector from './FixedTermDateSelector';
import { datesReducer, initialDatesState } from './helpers/datesReducer';
import type { Dates } from './helpers/datesReducer';
import type { GetListingCheckoutSummaryQuery } from '../../generated/graphql';
import CHECKOUT_SUMMARY from './listingDetailsCheckoutSummary.graphql';
import CHECKOUT_SUMMARY_MONTHLY from './listingDetailsMonthlyCheckoutSummary.graphql';

import styles from '../../../static/scss/components/listingDetails/bookingDetails.module.scss';

const BookingPanel = loadable(() => import('./BookingPanel'));
const BookSpaceModal = loadable(() => import('./BookSpaceModal'));

type Props = {
  t: (string, string) => string,
  isBookingModalOpen: boolean,
  onCloseBookingModal: () => void,
  listingId: number,
  onBeforeSubmit: () => void,
  extendable: boolean,
  acceptsMonthly: boolean,
  acceptsFixedTerm: boolean
};

type PriceLookupResponse = {
  data: any,
  loading: boolean,
  error: any
};

type BookingProps = {
  t: (string, string) => string,
  onTabChange: (number) => void,
  activeTab: number,
  acceptsFixedTerm: boolean,
  isOnlyMonthly: boolean,
  acceptsMonthly: boolean,
  listingId: number,
  onBeforeSubmit: () => void,
  dates: Dates,
  datesDispatch: () => void,
  fixedTermPrice: PriceLookupResponse,
  monthlyPrice: PriceLookupResponse
};

const BookingInformation = ({
  t,
  activeTab,
  onTabChange,
  acceptsMonthly,
  acceptsFixedTerm,
  isOnlyMonthly,
  onBeforeSubmit,
  listingId,
  dates,
  datesDispatch,
  fixedTermPrice,
  monthlyPrice
}: BookingProps) => (
  <div className={bem('tabs', 'container', {}, styles)}>
    {acceptsFixedTerm && acceptsMonthly && (
      <Tabs
        active={activeTab}
        theme="minimal-black"
        className={styles.tabs}
        onChange={onTabChange}
        evenSpacing
      >
        <TabItem cypress="booking-tab" size="secondary">
          {t('search:tabsHourlyDaily', 'Hourly / Daily')}
        </TabItem>
        <TabItem cypress="booking-tab" size="secondary">
          {t('search:tabsMonthly', 'Monthly')}
        </TabItem>
      </Tabs>
    )}

    {activeTab === 0 && !isOnlyMonthly && (
      <FixedTermDateSelector
        onBeforeSubmit={onBeforeSubmit}
        listingId={listingId}
        dates={dates.fixedTerm}
        datesDispatch={datesDispatch}
        price={fixedTermPrice}
      />
    )}

    {(activeTab === 1 || isOnlyMonthly) && (
      <MonthlyDateSelector
        onBeforeSubmit={onBeforeSubmit}
        listingId={listingId}
        dates={dates.monthly}
        datesDispatch={datesDispatch}
        price={monthlyPrice}
      />
    )}
  </div>
);

const useFixedTermPriceLookup = (
  startDateTime: ?Dayjs,
  endDateTime: ?Dayjs,
  id: number
) => {
  const [getCheckoutSummary, response]: [
    () => void,
    { loading: boolean, error: any, data: GetListingCheckoutSummaryQuery }
  ] = useLazyQuery(CHECKOUT_SUMMARY, {
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (startDateTime && endDateTime) {
      getCheckoutSummary({
        variables: {
          id,
          startDate: startDateTime && startDateTime.format('YYYY-MM-DD HH:mm'),
          endDate: endDateTime && endDateTime.format('YYYY-MM-DD HH:mm')
        }
      });
    }
  }, [startDateTime, endDateTime, getCheckoutSummary, id]);

  return response;
};

const useMonthlyPriceLookup = (
  startDateTime: ?Dayjs,
  monthlyType: string,
  id: number
) => {
  const [getMonthlyCheckoutSummary, response]: [
    () => void,
    { loading: boolean, error: any, data: GetListingCheckoutSummaryQuery }
  ] = useLazyQuery(CHECKOUT_SUMMARY_MONTHLY, {
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (startDateTime) {
      getMonthlyCheckoutSummary({
        variables: {
          id,
          startDate: startDateTime && startDateTime.format('YYYY-MM-DD HH:mm'),
          endDate: null,
          monthlyDuration: '0',
          monthlyApplicableDays: monthlyType.toLowerCase()
        }
      });
    }
  }, [startDateTime, getMonthlyCheckoutSummary, monthlyType, id]);

  return response;
};

const BookingDetails = ({
  t,
  isBookingModalOpen,
  onCloseBookingModal,
  listingId,
  onBeforeSubmit,
  extendable,
  acceptsMonthly,
  acceptsFixedTerm
}: Props) => {
  const [activeTab, onTabChange] = useQueryStringTab('booking_tab');

  const [datesState, datesDispatch] = useReducer(
    datesReducer,
    initialDatesState
  );

  const isOnlyMonthly = acceptsMonthly && !acceptsFixedTerm;
  const isMonthly = activeTab === 1 || isOnlyMonthly;
  const browser = useContext(BrowserContext);
  const isLarge = browser.greaterThan.medium;

  useEffect(() => {
    if (isLarge) {
      onCloseBookingModal();
    }
  }, [onCloseBookingModal, isLarge]);

  const {
    fixedTerm: { startDateTime, endDateTime },
    monthly: { startDateTime: monthlyStartDateTime, monthlyType }
  } = datesState;

  const fixedTermPrice = useFixedTermPriceLookup(
    startDateTime,
    endDateTime,
    listingId
  );

  const monthlyPrice = useMonthlyPriceLookup(
    monthlyStartDateTime,
    monthlyType,
    listingId
  );

  const information = (
    <BookingInformation
      t={t}
      activeTab={activeTab}
      onTabChange={onTabChange}
      acceptsMonthly={acceptsMonthly}
      acceptsFixedTerm={acceptsFixedTerm}
      isOnlyMonthly={isOnlyMonthly}
      onBeforeSubmit={onBeforeSubmit}
      listingId={listingId}
      dates={datesState}
      datesDispatch={datesDispatch}
      fixedTermPrice={fixedTermPrice}
      monthlyPrice={monthlyPrice}
    />
  );

  return (
    <>
      <BookingPanel isMonthly={isMonthly} t={t} extendable={extendable}>
        {information}
      </BookingPanel>

      <BookSpaceModal
        t={t}
        isOpen={isBookingModalOpen}
        onClose={onCloseBookingModal}
        isMonthly
      >
        {information}
      </BookSpaceModal>
    </>
  );
};

export default BookingDetails;
